import { v4 as uuid } from "uuid";

import type { DocumentScanEntryPoint } from "@medbillai/utils/documentScan";
import type { UploadMethod } from "@medbillai/utils/documentUpload";

import type { UploadFunnelEventBase } from "./types";
import { getUploadPlatform } from "./uploadPlatform";

/** The funnel envelope, minted when the upload sheet opens. */
export type UploadFunnelSession = UploadFunnelEventBase;

/**
 * A session frozen against the method the user picked. Travels explicitly with
 * the files — through `onFilesLoaded`, the form engine, and into `upload()` —
 * rather than through shared mutable state, so a sheet reopened mid-batch can
 * never re-attribute an in-flight upload's events or persisted provenance.
 */
export type UploadFunnelAttempt = UploadFunnelEventBase & {
  method: UploadMethod;
};

/**
 * Total batch size, or `undefined` when any file reported an unknown size —
 * a partial sum would read as a small upload and skew throughput.
 */
export const sumDefinedSizes = (
  sizes: (number | undefined)[],
): number | undefined =>
  sizes.some(size => size === undefined)
    ? undefined
    : sizes.reduce<number>((total, size) => total + (size ?? 0), 0);

export const createUploadFunnelSession = (params: {
  entryPoint: DocumentScanEntryPoint;
  issueId?: string;
}): UploadFunnelSession => ({
  uploadSessionId: uuid(),
  entryPoint: params.entryPoint,
  platform: getUploadPlatform(),
  ...(params.issueId !== undefined ? { issueId: params.issueId } : {}),
});
